'use client';

import { useContext, useEffect, useRef } from 'react';
import {
  buildCubeCatalog,
  buildCubeSelections,
  catalogKey,
  type CubeSelections,
  diffCubeSelections
} from '@/new-cars/Tdc/cubeSelections';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type useTestDriveCenter from '~/hooks/new-cars/useTestDriveCenter';

// Streams the customer's options-stage choices to the paired cube display so it
// can show the live build. Takes the container's `useTestDriveCenter` instance
// (rather than calling the hook itself) so it shares the real isSent state.
export default function useCubeSelectionSync(
  testDriveCenter: ReturnType<typeof useTestDriveCenter>
) {
  const { customizeOptions, selectedOptions, selectedParentModel } =
    useContext(ParentModelContext);
  const { isActive, isPairing, isSent, sendCube } = testDriveCenter;

  const isLoaded = customizeOptions.isLoaded;
  const shouldSync = isActive && !isPairing && !isSent;
  const make = selectedParentModel.make || customizeOptions.data.make;
  const model = selectedParentModel.model || customizeOptions.data.model;
  const trim = customizeOptions.data.trim || selectedOptions?.trim || null;

  // Preload: once the options stage has loaded, push the full option catalog
  // (with image URLs) so the cube can prefetch every image up front —
  // per-selection events then render instantly from its cache. Re-sent whenever
  // the available options change (e.g. a trim switch), but skipped when the same
  // catalog reloads.
  const previousCatalogKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!shouldSync || !isLoaded) return;

    const catalog = buildCubeCatalog(customizeOptions.data);
    const key = catalogKey(catalog);

    if (previousCatalogKeyRef.current === key) return;

    previousCatalogKeyRef.current = key;
    sendCube({ catalog, make, model, trim });
  }, [
    customizeOptions.data,
    isLoaded,
    make,
    model,
    sendCube,
    shouldSync,
    trim
  ]);

  // Per-selection: emit the full current build (plus which fields changed) every
  // time the committed selection moves — covering both direct picks and
  // AutoSelect's multi-field resolutions. Deduped so unrelated context updates
  // don't re-fire an identical snapshot.
  const previousSelectionsRef = useRef<CubeSelections | null>(null);
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (!shouldSync || !isLoaded) return;

    const next = buildCubeSelections(selectedOptions, customizeOptions.data);
    const changed = diffCubeSelections(previousSelectionsRef.current, next);

    if (hasSyncedRef.current && changed.length === 0) return;

    previousSelectionsRef.current = next;
    hasSyncedRef.current = true;
    sendCube({ changed, make, model, selections: next, trim });
  }, [
    customizeOptions.data,
    isLoaded,
    make,
    model,
    selectedOptions,
    sendCube,
    shouldSync,
    trim
  ]);
}
